<template>
  <div>
    <a-modal class="badge-modal" v-model="visible" title="Badge">
      <div class="title mb-24px">{{ $t(`btn.${mode}_badge`) }}</div>

      <a-alert
          v-if="mode === 'create' && remoteImportEnabled"
          class="mb-24px"
          type="info"
          show-icon
          :message="$t('text.remote_import_badge_notice')"
      />

      <a-row :gutter="32">
        <a-col class="gutter-row" :xs="24" :md="12">
          <form-input
              class="mb-24px"
              :label="$t('label.nfc_id')"
              :placeholder="$t('label.nfc_id')"
              :value="form.nfc"
              :required="mode === 'create'"
              :disabled="mode === 'update'"
              :error="error.nfc"
              @input="form.nfc = $event"
          />
          <form-input
              class="mb-24px"
              :label="$t('label.first_name')"
              :placeholder="$t('label.first_name')"
              :value="form.holder_first_name"
              :error="error.holder_first_name"
              @input="form.holder_first_name = $event"
          />
          <form-input
              class="mb-24px"
              :label="$t('label.last_name')"
              :placeholder="$t('label.last_name')"
              :value="form.holder_last_name"
              :error="error.holder_last_name"
              @input="form.holder_last_name = $event"
          />
        </a-col>
        <a-col class="gutter-row" :xs="24" :md="12">
          <form-input
              class="mb-24px"
              :label="$t('label.email')"
              :placeholder="$t('label.email')"
              :value="form.holder_email"
              :error="error.holder_email"
              @input="form.holder_email = $event"
          />
          <form-input
              class="mb-24px"
              :label="$t('label.external_id')"
              :placeholder="$t('label.external_id')"
              :value="form.external_id"
              :error="error.external_id"
              @input="form.external_id = $event"
          />
        </a-col>
      </a-row>

      <template slot="footer">
        <a-button key="back" class="app-button" @click="visible = false">
          {{ $t('btn.cancel') }}
        </a-button>
        <a-button key="submit" class="app-button primary" :loading="loading" @click="submit">
          {{ mode === 'create' ? $t('btn.create_badge') : $t('btn.update_badge') }}
        </a-button>
      </template>
    </a-modal>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import FormInput from '@/components/elements/FormInput';

export default {
  components: {FormInput},
  props: {
    show: {type: Boolean, default: false},
    badgeReference: {type: String, default: null},
    organizationId: {type: [Number, String], default: null},
  },
  data: () => ({
    loading: false,
    visible: false,
    mode: 'create',
    form: {
      nfc: null,
      holder_first_name: null,
      holder_last_name: null,
      holder_email: null,
      external_id: null,
    },
    error: {
      nfc: null,
      holder_first_name: null,
      holder_last_name: null,
      holder_email: null,
      external_id: null,
    },
  }),
  computed: {
    ...mapGetters({
      badge: 'badgesStore/BADGE',
      remoteImportEnabled: 'badgesStore/REMOTE_IMPORT_ENABLED',
    }),
  },
  watch: {
    show(value) {
      this.visible = value;
    },
    visible(value) {
      if (!value) {
        this.clearForm();
        this.clearError();
        this.$emit('close', true);
      }
    },
    badgeReference(reference) {
      this.mode = reference ? 'update' : 'create';
      if (reference) {
        this.$store.dispatch('badgesStore/GET_BADGE', reference);
      }
    },
    badge: {
      handler(data) {
        if (data && this.mode === 'update') {
          this.form.nfc = data.nfc;
          this.form.holder_first_name = data.holder_first_name;
          this.form.holder_last_name = data.holder_last_name;
          this.form.holder_email = data.holder_email;
          this.form.external_id = data.external_id;
        }
      },
      deep: true,
    },
  },
  methods: {
    clearForm() {
      Object.keys(this.form).forEach(k => (this.form[k] = null));
    },
    clearError() {
      Object.keys(this.error).forEach(k => (this.error[k] = null));
    },
    validate() {
      this.clearError();
      if (this.mode === 'create' && !this.form.nfc) {
        this.error.nfc = this.$t('error.field_required');
      }
      return !Object.values(this.error).some(v => v);
    },
    submit() {
      if (!this.validate()) return;
      this.loading = true;

      const request = this.mode === 'create'
          ? this.$store.dispatch('badgesStore/CREATE_BADGE', {...this.form, organization_id: this.organizationId})
          : this.$store.dispatch('badgesStore/UPDATE_BADGE', {
            reference: this.badgeReference,
            data: {
              holder_first_name: this.form.holder_first_name,
              holder_last_name: this.form.holder_last_name,
              holder_email: this.form.holder_email,
              external_id: this.form.external_id,
            },
          });

      request
          .then(() => {
            this.loading = this.visible = false;
            this.$message.success(this.$t(this.mode === 'create' ? 'alert.badge_created' : 'alert.badge_updated'));
          })
          .catch(error => {
            this.loading = false;
            if (error.response && error.response.status === 422) {
              const errors = error.response.data.error.errors;
              for (const key of Object.keys(errors)) {
                if (key in this.error) {
                  this.error[key] = errors[key][0];
                } else {
                  this.$message.error(errors[key][0]);
                }
              }
            } else {
              this.$message.error(
                  (error.response && error.response.data.error && error.response.data.error.message)
                  || this.$t('alert.something_goes_wrong')
              );
            }
          });
    },
  },
};
</script>

<style lang="scss" scoped>
.badge-modal::v-deep {
  .ant-modal {
    width: 700px !important;

    .title {
      color: #000;
      font-size: 28px;
      font-weight: 500;
      text-align: center;
    }
  }
}
</style>
